import { CoverPicture, ErrorSummary, LoadingOverlayWrapper, RightSideModal, RightSideModalTitle } from 'components';
import { IAppointmentWithCustomer } from 'models/shop/appointments';
import * as c from 'models/constants';
import * as React from 'react';
import { IStore } from 'redux/IStore';
import { Dispatch, connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { ITranslateProps, InjectedFormProps } from 'models/common';
import { BookingCustomer } from 'containers/Shop/Common/BookingCustomer';
import { InjectedManualBookingModalProps, ManualBookingScreen } from '..';
import { translate } from 'react-i18next';
import { IManualBookingFormData } from 'models/shop/manualbooking';
import { getOneShopCustomer } from 'redux/modules/shop/shopcard';
import { ICustomer } from 'models/shop/customer';
import { RightSideModalFooter } from 'components/RightSideModal';
import { Field } from 'redux-form';
import { BookingDatePicker, BookingTextareaField } from 'components/FormControls';
import { maxLength, required } from 'helpers/validationattributes';
import { addURLSearchParams, formatCurrency, formatDuration, thumbUrl, timePickerProps } from 'helpers/common';
import moment = require('moment');
import renderIcon, { IconName } from 'helpers/renderIcon';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import { DropdownModal } from 'components/Common/DropdownModal';
import { PortalPageSection } from 'components/PortalStructure';
import { IOfficeMember } from 'models/shop/members';
import {
	generateFrequencyItems,
	generateNewBookingItemTimes,
	getPriceFromBookingItem,
	minBookingItemSchedule,
} from '../helpers';
import { IAddBookingItemRequest } from 'models/bookappointment';
import { OrderPaymentStatus } from 'models';
const classNames = require('classnames');
require('./style.scss');

interface IOwnProps {
	appointment: IAppointmentWithCustomer;
	officeId?: string;
	mode?: 'add' | 'edit';
}

interface IState {
	mainScreenCurrentTab: string;
	isEditingAppointmentNote: boolean;
	isSubmitting: boolean;
}

type IProps = IOwnProps &
	ReturnType<typeof stateToProps> &
	ReturnType<typeof dispatchToProps> &
	ITranslateProps &
	InjectedFormProps &
	InjectedManualBookingModalProps;

const IconButton = ({
	icon,
	children,
	onClick,
	className,
}: {
	icon: IconName;
	children?: any;
	onClick?: any;
	className?: string;
}) => {
	return (
		<span onClick={onClick} className={className}>
			{renderIcon(icon)}
			{children}
		</span>
	);
};

const maxLength500 = maxLength(500);

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class MainBookingScreenComponent extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.handleMainScreenTabSelect = this.handleMainScreenTabSelect.bind(this);
		this.handleDateTimeChange = this.handleDateTimeChange.bind(this);
		this.renderSelectedServices = this.renderSelectedServices.bind(this);

		this.calculateTimeOfTheAppointment = this.calculateTimeOfTheAppointment.bind(this);

		this.state = {
			mainScreenCurrentTab: 'appointmentDetails',
			isEditingAppointmentNote: false,
			isSubmitting: false,
		};
	}

	/**
	 * check booking items and reorder if start time is before the others, change time of the appointment to the earliest time
	 */
	calculateTimeOfTheAppointment() {
		const mbis = minBookingItemSchedule(this.props.currentFormValues.bookingItems);

		if (mbis.minSchedule.toISOString() !== this.props.currentFormValues.startTimeOfTheAppointment) {
			// change start time and date
			this.props.change('startTimeOfTheAppointment', mbis.minSchedule.toISOString());
			this.props.change('dateOfTheAppointment', mbis.minSchedule.toISOString());
			addURLSearchParams('time', mbis.minSchedule.toISOString());

			// create deep copy, sort booking items by schedule
			const bookingItems: IAddBookingItemRequest[] = JSON.parse(
				JSON.stringify(this.props.currentFormValues.bookingItems),
			);
			bookingItems.sort((a, b) => moment(a.schedule).unix() - moment(b.schedule).unix());
			this.props.change('bookingItems', bookingItems);
		}
	}

	componentDidMount(): void {
		const { shopcard } = this.props;
		// check if customer data is loaded
		const customerId = this.props.currentFormValues?.customerId;
		if (this.props.currentFormValues?.customerId) {
			if (
				shopcard.selectedCustomer?.id != this.props.currentFormValues?.customerId &&
				shopcard?.customers?.items?.findIndex((customer) => customer?.id == customerId) == -1
			) {
				this.props.getOneShopCustomer(this.props.currentFormValues?.customerId);
			}
		}

		if (this.props.currentFormValues?.bookingItems?.length) {
			this.calculateTimeOfTheAppointment();
		}
	}

	componentDidUpdate(prevProps: Readonly<IProps>): void {
		if (!prevProps?.currentFormValues?.bookingItems?.length && this.props.currentFormValues?.bookingItems?.length) {
			this.calculateTimeOfTheAppointment();
		}
	}

	handleMainScreenTabSelect(mainScreenCurrentTab: string) {
		this.setState({ mainScreenCurrentTab });
	}

	handleDateTimeChange(value: string, type: 'time' | 'date') {
		const oldDateTime = momentWithTimezone(this.props.timezone, this.props.time);
		const newDateTime = momentWithTimezone(this.props.timezone, value);

		let newDate: moment.Moment;
		let newTimeUnix: number;

		// If the date changed then take the old time and new date
		if (type === 'date') {
			newDate = newDateTime.startOf('day');
			newTimeUnix = oldDateTime.unix() - oldDateTime.startOf('day').unix();
		} else {
			newDate = oldDateTime.startOf('day');
			newTimeUnix = newDateTime.unix() - newDateTime.startOf('day').unix();
		}

		newDate = newDate.clone().add(newTimeUnix, 'seconds');

		// Relatively update time of booking items (send new and old date, first booking item gets the new date, others get newDate + difference between newDate and oldDate)
		if (this.props.currentFormValues?.bookingItems?.length) {
			const oldDate = moment(this.props.time);
			const updatedBookingItems = generateNewBookingItemTimes(
				this.props.currentFormValues.bookingItems,
				newDate,
				oldDate,
			);

			this.props.change('bookingItems', updatedBookingItems);
		}

		addURLSearchParams('time', newDate.toISOString());
	}

	renderSelectedServices() {
		const { currentFormValues, t } = this.props;
		const servicesElement: JSX.Element[] = [];
		const currentBookingItems = currentFormValues?.bookingItems || [];
		const isEditModeOff = this.props.mode === 'edit' && !this.props.currentFormValues?.isEditModeEnabled;

		currentBookingItems?.forEach((bi, idx) => {
			// To keep the correct idx
			if (!bi?.deleted) {
				// find the correlating booking template
				const bt = this.props.shopBookingTemplates?.servicesById?.[bi?.bookingTemplateId] || null;
				const sg = this.props.serviceGroups?.serviceGroupsById?.[bt?.serviceGroupId] || null;
				const parentSg = this.props.serviceGroups?.serviceGroupsById?.[sg?.parentId] || null;
				const resource = this.props?.currentOffice.staff.find((res) => res.id === bi.resourceId) || null;
				const fullName = resource ? resource.name.split(' ') : null;

				const startTime = momentWithTimezone(this.props?.currentOffice?.zoneInfo, bi.schedule) || null;
				const bookingConflict =
					currentFormValues?.bookingItemConflicts?.length && bt
						? currentFormValues?.bookingItemConflicts?.find((bic) => bic.bookingTemplateId === bt.id)
						: null;

				if (bt) {
					const duration = bi?.duration || bt.duration;

					const servicePrice = (baseClassName) => {
						const originalPrice = bt.originalPrice;
						const newPrice = getPriceFromBookingItem(bi, bt);
						return (
							<>
								{originalPrice != newPrice && (
									<span className={`${baseClassName}__old`}>
										{formatCurrency(this.props?.manageShop?.shop?.currencyKey, bt.originalPrice)}
									</span>
								)}
								<span className={`${baseClassName}__new`}>
									{formatCurrency(this.props?.manageShop?.shop?.currencyKey, newPrice)}
								</span>
							</>
						);
					};

					const serviceElement: JSX.Element = (
						<PortalPageSection
							key={bt.id + resource?.id}
							onClick={() =>
								!isEditModeOff &&
								this.props.setScreen(ManualBookingScreen.EditService, { bookingItemIndex: idx.toString() })
							}
							className={`main-booking-screen__serviceWrapper ${!isEditModeOff ? 'editable' : ''}`}
						>
							<p className="main-booking-screen__service__title">
								{parentSg?.value} <span className="main-booking-screen__service__titleSeparator">/</span> {bt?.name}
							</p>
							{bi?.childrenAddBookingItemRequest?.length ? (
								<div className="main-booking-screen__bundle__infoWrapper">
									<div className="main-booking-screen__service__serviceInfoWrapper">
										<div className="main-booking-screen__service__time">
											{startTime.format('HH:mm') + ' - ' + startTime.clone().add(duration, 'seconds').format('HH:mm')}{' '}
											<span className="main-booking-screen__service__timeMinutes">({formatDuration(duration)})</span>
										</div>
									</div>
									{bi.childrenAddBookingItemRequest.map((cbir, i) => {
										// find the correlating booking template
										const childBt = this.props.shopBookingTemplates?.servicesById?.[cbir.bookingTemplateId] || null;
										const childSg = this.props.serviceGroups?.serviceGroupsById?.[childBt?.serviceGroupId] || null;
										const parentChildSg = this.props.serviceGroups?.serviceGroupsById?.[childSg?.parentId] || null;
										const childResource =
											this.props?.currentOffice.staff.find((res) => res.id === cbir.resourceId) || null;
										const childFullName = childResource ? childResource.name.split(' ') : null;
										const childDuration = cbir?.duration || childBt.duration;

										return (
											<div key={cbir.bookingTemplateId + i} className="main-booking-screen__bundle__childWrapper">
												<p className="main-booking-screen__bundle__childTitle">
													{parentChildSg?.value}{' '}
													<span className="main-booking-screen__bundle__childTitleSeparator">/</span> {childBt?.name}
												</p>
												<div className="main-booking-screen__bundle__desktop__childInfoWrapper">
													<div className="main-booking-screen__bundle__desktop__memberWrapper">
														<CoverPicture
															imageUrl={thumbUrl(childResource?.picture, 'medium') || ''}
															icon={childResource ? null : renderIcon('warning')}
															hasPicture={Boolean(childResource?.picture)}
														/>
														<div className="main-booking-screen__bundle__desktop__memberInfoWrapper">
															<p className="main-booking-screen__bundle__desktop__memberName">
																{childFullName?.[0]} {childFullName?.[childFullName?.length - 1].charAt(0)}
																{childFullName?.[childFullName?.length - 1]?.length > 1 && '.'}
															</p>
															<p className="main-booking-screen__bundle__desktop__memberTitle">
																{(childResource as IOfficeMember)?.jobTitle?.substring(0, 25)}
															</p>
														</div>
													</div>
													<div className="main-booking-screen__bundle__desktop__serviceInfoWrapper">
														<div className="main-booking-screen__bundle__desktop__time">
															{formatDuration(childDuration)}
														</div>
													</div>
												</div>
												<div className="main-booking-screen__bundle__mobile__childInfoWrapper">
													<span className="main-booking-screen__bundle__mobile__memberName">
														{childFullName?.[0]} {childFullName?.[childFullName?.length - 1].charAt(0)}.
													</span>
													<span className="main-booking-screen__bundle__mobile__time">
														{formatDuration(childDuration)}
													</span>
												</div>
											</div>
										);
									})}
									<div className="main-booking-screen__bundle__price">
										{servicePrice('main-booking-screen__bundle__price')}
									</div>
								</div>
							) : (
								<>
									<div className="main-booking-screen__service__desktop__infoWrapper">
										<div className="main-booking-screen__service__desktop__memberWrapper">
											<CoverPicture
												imageUrl={thumbUrl(resource?.picture, 'medium') || ''}
												hasPicture={Boolean(resource?.picture)}
											/>
											<div className="main-booking-screen__service__desktop__memberInfoWrapper">
												<p className="main-booking-screen__service__desktop__memberName">
													{fullName?.[0]} {fullName?.[fullName?.length - 1].charAt(0)}.
												</p>
												<p className="main-booking-screen__service__desktop__memberTitle">
													{(resource as IOfficeMember)?.jobTitle?.substring(0, 25)}
												</p>
											</div>
										</div>
										<div className="main-booking-screen__service__mobile__serviceInfoWrapper">
											<div className="main-booking-screen__service__desktop__time">
												{startTime.format('HH:mm') + ' - ' + startTime.clone().add(duration, 'seconds').format('HH:mm')}{' '}
												<span className="main-booking-screen__service__timeMinutes">({formatDuration(duration)})</span>
											</div>
											<div className="main-booking-screen__service__desktop__price">
												{servicePrice('main-booking-screen__service__desktop__price')}
											</div>
										</div>
									</div>
									<div className="main-booking-screen__service__mobile__infoWrapper">
										<div className="main-booking-screen__service__mobile__time">
											{startTime.format('HH:mm') + ' - ' + startTime.clone().add(duration, 'seconds').format('HH:mm')}{' '}
											<span className="main-booking-screen__service__mobile__timeMinutes">
												({formatDuration(duration)})
											</span>
										</div>
										<div className="main-booking-screen__service__mobile__memberInfoWrapper">
											<span className="main-booking-screen__service__mobile__memberName">
												{fullName?.[0]} {fullName?.[fullName?.length - 1].charAt(0)}.
											</span>
											<span className="main-booking-screen__service__mobile__price">
												{servicePrice('main-booking-screen__service__mobile__price')}
											</span>
										</div>
									</div>
								</>
							)}

							{bookingConflict && (
								<>
									<div className="main-booking-screen__bookingConflictPadding"></div>
									<div className="main-booking-screen__bookingConflictWrapper">
										{renderIcon('warning')}
										<span className="main-booking-screen__bookingConflictName">
											{t(
												`ManualBookingModal_MainScreen_BookingConflict_${Number(
													bookingConflict.unavailableBookingReason,
												)}`,
												{ resourceName: resource?.name },
											)}
										</span>
									</div>
								</>
							)}
						</PortalPageSection>
					);

					servicesElement.push(serviceElement);
				}
			}
		});

		return servicesElement;
	}

	get customerData(): ICustomer {
		const customerId = this.props.currentFormValues?.customerId;
		if (!customerId && this.props.mode === 'add') {
			return;
		}

		// Walk-in customer
		if (this.props.mode === 'edit' && this.props.shopcard?.selectedCustomer?.email === 'walk-in@glossiq.com') {
			return { ...this.props.shopcard.selectedCustomer, email: undefined };
		}

		if (this.props.shopcard.selectedCustomer?.id == this.props.currentFormValues?.customerId) {
			return this.props.shopcard.selectedCustomer;
		}

		return this.props.shopcard?.customers?.items?.find((customer) => customer?.id == customerId);
	}

	public render() {
		const { setScreen, t, timezone, shopOrder } = this.props;
		const appointmentNote: string = this.props?.currentFormValues?.appointmentNote;
		const customerData = this.customerData;
		const frequencyItems = generateFrequencyItems();
		const isEditModeOff = this.props.mode === 'edit' && !this.props.currentFormValues?.isEditModeEnabled;
		let numberSet = null;

		if (appointmentNote?.length && customerData?.notes?.length) {
			numberSet = 2;
		} else if (appointmentNote?.length || customerData?.notes?.length) {
			numberSet = 1;
		}

		const mainTabs = [
			{ key: 'appointmentDetails', translation: t('Shop_Appointments_AppointmentDetails') },
			{ key: 'notes', translation: t('User_UserCard_NotesTitle'), numberSet },
		];

		let tabContent: JSX.Element = null;
		let customerNote: string = t('Shop_Appointments_SelectACustomer');
		let appointmentNoteElement: JSX.Element = null;

		if (customerData?.notes) {
			customerNote = customerData.notes;
		} else if (customerData) {
			customerNote = t('Shop_Appointments_CustomerHasNoNotes');
		}

		if (!this.state.isEditingAppointmentNote) {
			if (!appointmentNote?.length) {
				appointmentNoteElement = !isEditModeOff && (
					<div
						className="main-booking-screen__appointmentNotes__addAppointmentNote"
						onClick={() => this.setState({ isEditingAppointmentNote: true })}
					>
						{renderIcon('nikaPlus', 20, 20, 'main-booking-screen__appointmentNotes__icon')}
						<span className="main-booking-screen__appointmentNotes__text">{t('Shop_Appointments_AddNote')}</span>
					</div>
				);
			} else {
				const noteDropdownOptions = [
					{
						text: t('BookAppointment_Review_EditNote'),
						onClick: () => this.setState({ isEditingAppointmentNote: true }),
					},
					{
						text: t('BookAppointment_Review_DeleteNote'),
						onClick: () => this.props.change('appointmentNote', null),
						className: 'red',
					},
				];

				appointmentNoteElement = (
					<div className="main-booking-screen__appointmentNotes__appointmentNoteContainer">
						<div className="main-booking-screen__appointmentNotes__appointmentNote">{appointmentNote}</div>
						{!isEditModeOff && (
							<DropdownModal
								className="forceLeft"
								buttonComponent={renderIcon('moreVerticalGray', 24, 24)}
								stopPropagation={true}
							>
								{noteDropdownOptions.map((option, i) => (
									<div
										className={option.className}
										key={`booking-customer-option-${i}`}
										onClick={() => option?.onClick()}
									>
										{option.text}
									</div>
								))}
							</DropdownModal>
						)}
					</div>
				);
			}
		} else {
			appointmentNoteElement = (
				<>
					<Field
						className="main-booking-screen__appointmentNotes__appointmentNoteField"
						name="appointmentNote"
						component={BookingTextareaField}
						type="textarea"
						rows={7}
						validate={[maxLength500]}
						maxLength={500}
						information={t('BookAppointment_Review_NewAppointmentNote')}
						hideCounter={true}
					/>
					<div className="main-booking-screen__appointmentNotes__button-row main-booking-screen__appointmentNotes__button-row--right">
						<IconButton
							icon={'cancel'}
							className="main-booking-screen__appointmentNotes__iconButton main-booking-screen__appointmentNotes__iconButton--red"
							onClick={() => {
								this.props.change('appointmentNote', '');
								this.setState({ isEditingAppointmentNote: false });
							}}
						>
							{t('BSPortal_Common_Cancel')}
						</IconButton>
						<IconButton
							icon={'check'}
							className={classNames(
								'main-booking-screen__appointmentNotes__iconButton main-booking-screen__appointmentNotes__iconButton--green',
								{
									'main-booking-screen__appointmentNotes__iconButton--disabled':
										!this.props.currentFormValues?.appointmentNote?.length,
								},
							)}
							onClick={() => {
								this.setState({ isEditingAppointmentNote: false });
							}}
						>
							{t('BookAppointment_Review_SaveNote')}
						</IconButton>
					</div>
				</>
			);
		}

		if (this.state.mainScreenCurrentTab === 'appointmentDetails') {
			tabContent = (
				<>
					<div className="row">
						<div className="col-12">
							<h3 className="main-booking-screen__sectionTitle firstAppTitle">{t('Shop_Appointments_DateTime')}</h3>
						</div>
						<div className="col-md-8 col-12">
							<div className={`main-booking-screen__dateFieldWrapper ${isEditModeOff ? 'fieldDisabled' : ''}`}>
								<Field
									name={'dateOfTheAppointment'}
									type="text"
									component={BookingDatePicker}
									label={t('Shop_Appointments_DateOfTheAppointment')}
									information={t('Shop_Appointments_DateOfTheAppointment')}
									validate={[required]}
									hideYearArrows={true}
									timezone={timezone}
									onChange={(_event, newValue) => this.handleDateTimeChange(newValue, 'date')}
									disabled={isEditModeOff}
									minDate={momentWithTimezone(timezone).startOf('day').toDate()} // cannot create appointment in the past
								/>
							</div>
						</div>
						<div className="col-md-4 col-12">
							<div className={`main-booking-screen__timeFieldWrapper ${isEditModeOff ? 'fieldDisabled' : ''}`}>
								<Field
									name={'startTimeOfTheAppointment'}
									type="text"
									component={BookingDatePicker}
									information={t('Shop_Appointments_TimeOfTheAppointment')}
									label={t('Shop_Appointments_TimeOfTheAppointment')}
									{...timePickerProps(moment().startOf('day').toDate(), moment().endOf('day').toDate())}
									timezone={timezone}
									onChange={(_event, newValue) => this.handleDateTimeChange(newValue, 'time')}
									disabled={isEditModeOff}
								/>
							</div>
						</div>
						<div className="col-12">
							{this.props.currentFormValues?.frequency &&
							this.props.currentFormValues?.endsAt &&
							this.props.currentFormValues?.lastTime ? (
								this.props.currentFormValues?.updateOnlySelectedItem &&
								this.props.mode === 'edit' &&
								this.props?.currentFormValues?.isPaidOnline ? null : (
									<div className="main-booking-screen__recurringEventContainer">
										<span>
											{t('ManualBookingModal_MainScreen_RecurringEventRepeats', {
												frequency: frequencyItems
													.find((fi) => fi.key === this.props.currentFormValues.frequency)
													.value.toLowerCase(),
												endDate: momentWithTimezone(this.props.timezone, this.props.currentFormValues?.lastTime).format(
													'LL',
												),
											})}
										</span>
										{!isEditModeOff && (
											<span
												className="main-booking-screen__recurringEventButton"
												onClick={() => {
													!isEditModeOff && this.props.setScreen(ManualBookingScreen.RecurringEvent);
												}}
											>
												{t('BSPortal_Common_Edit')}
											</span>
										)}
									</div>
								)
							) : !isEditModeOff && !this.props?.currentFormValues?.isPaidOnline ? (
								<div className="main-booking-screen__recurringEventContainer">
									<span
										className="main-booking-screen__recurringEventButton"
										onClick={() => {
											this.props.setScreen(ManualBookingScreen.RecurringEvent);
										}}
									>
										{t('Shop_Appointments_SetRecurringEvent')}
									</span>
								</div>
							) : null}
						</div>
						<div className="col-12">
							<div className="main-booking-screen__servicesContainer">
								<h3 className="main-booking-screen__sectionTitle">{t('User_ShopCard_Services')}</h3>
								<div className="main-booking-screen__servicesWrapper">{this.renderSelectedServices()}</div>
								{!isEditModeOff && !this.props?.currentFormValues?.isPaidOnline ? (
									<div
										onClick={() => setScreen(ManualBookingScreen.SelectService, { serviceSelectMode: 'add' })}
										className="main-booking-screen__addServices"
									>
										{renderIcon('nikaPlus', 20, 20, 'main-booking-screen__addServices__icon')}
										<span className="main-booking-screen__addServices__text">
											{t('User_ShopCard_Services_AddServices')}
										</span>
									</div>
								) : null}
							</div>
						</div>
					</div>
				</>
			);
		} else if (this.state.mainScreenCurrentTab === 'notes') {
			tabContent = (
				<>
					<div className="row">
						<div className="col-12">
							<h3 className="main-booking-screen__sectionTitle firstNotTitle">{t('Shop_Appointments_ClientNotes')}</h3>
							<div className="main-booking-screen__clientNotes">{customerNote}</div>
							<h3 className="main-booking-screen__sectionTitle">
								{t('BookAppointment_Review_Title_AppointmentNotes')}
							</h3>
							<div className="main-booking-screen__appointmentNotes">{appointmentNoteElement}</div>
						</div>
					</div>
				</>
			);
		} else {
			return null;
		}

		let orderStatusElement: JSX.Element = null;

		if (
			shopOrder?.order?.paymentStatus === OrderPaymentStatus.Verified ||
			shopOrder?.order?.message?.includes('Original paid order id') === true
		) {
			orderStatusElement = <span className="paid-online">{t('User_Order_PaidOnline')}</span>;
		} else if (shopOrder?.order?.paymentStatus === OrderPaymentStatus.Rejected) {
			orderStatusElement = <span className="payment-rejected">{t('User_Order_PaymentRejected')}</span>;
		} else {
			orderStatusElement = <span className="payment-at-salon">{t('User_Order_PaymentAtSalon')}</span>;
		}

		let areOrderItemsInThePast: boolean = false;

		// check if any of the existing booking items are in the past and disable save button as BE currently does not allow bookings in the past
		if (this.props.mode === 'edit' && this.props?.currentFormValues?.bookingItems?.length) {
			for (let cIdx = 0; cIdx < this.props?.currentFormValues?.bookingItems.length; cIdx = cIdx + 1) {
				const bi = this.props?.currentFormValues?.bookingItems[cIdx];
				if (momentWithTimezone(timezone, bi.schedule).isBefore(momentWithTimezone(timezone))) {
					areOrderItemsInThePast = true;
					break;
				}
			}
		}

		return (
			<RightSideModal
				size="small"
				header={
					<>
						<RightSideModalTitle
							title={
								this.props?.mode === 'edit' ? (
									<h2 className="right-side-title__title">{t('ManualBookingModal_MainScreen_Edit_Main_Title')}</h2>
								) : (
									t('ManualBookingModal_BookingCustomer_Main_Title')
								)
							}
							closeModalClick={
								(this.props.mode === 'add' && !this.props.dirty) ||
								(this.props?.mode === 'edit' && !this.props?.currentFormValues?.isEditModeEnabled)
									? this.props.closeModal
									: this.props.startCloseModal
							}
						/>
					</>
				}
				footer={
					<>
						<RightSideModalFooter
							rightButtonClick={() => {
								if (this.props.mode === 'add' && !this.state.isSubmitting) {
									this.setState({ isSubmitting: true });
									this.props
										.submitAddManualBooking()
										.then((res: { status: 'ok' | 'error'; message: string }) => {
											console.info('Res: ', res);
											if (res?.status === 'ok') {
												// TODO: show success message
												this.props.closeModal();
											}
										})
										.catch((err) => {
											this.setState({ isSubmitting: false });
											console.error('Err: ', err);
										});
								} else if (this.props.mode === 'edit') {
									if (this.props.currentFormValues?.isEditModeEnabled && !this.state.isSubmitting) {
										this.setState({ isSubmitting: true });
										// if updating recurring event and the choice to update only selected appointment or all upcoming has not been set then display the update screen
										if (
											this.props?.shopOrder?.items?.[0]?.recurringBookingId &&
											!Object.prototype.hasOwnProperty.call(this.props.currentFormValues, 'updateOnlySelectedItem')
										) {
											this.props.setScreen(ManualBookingScreen.UpdateRecurringSelection);
										} else {
											// else submit
											this.props
												.submitEditManualBooking()
												.then((res: { status: 'ok' | 'error'; message: string }) => {
													console.info('Res: ', res);
													if (res?.status === 'ok') {
														// TODO: show success message
														this.props.closeModal();
													}
												})
												.catch((err) => {
													this.setState({ isSubmitting: false });
													console.error('Err: ', err);
												});
										}
									} else {
										this.props.change('isEditModeEnabled', true);
									}
								} else {
									console.error('Already submitting');
								}
							}}
							rightButtonText={isEditModeOff ? t('BSPortal_Common_Edit') : t('BSPortal_Common_Save')}
							isRightButtonDisabled={
								!this.props?.currentFormValues?.bookingItems?.filter((x) => !x.deleted)?.length ||
								this.state.isSubmitting ||
								areOrderItemsInThePast
							}
							isRightButtonHidden={areOrderItemsInThePast}
							rightButtonIcon={isEditModeOff ? 'editPencil' : undefined}
							rightButtonCustomClass={isEditModeOff ? 'editEnable' : ''}
							leftButtonClick={
								this.props?.mode === 'edit'
									? () => {
											this.props.setScreen(ManualBookingScreen.CancelAppointment);
									  }
									: null
							}
							leftButtonText={this.props?.mode === 'edit' ? t('Orders_UserOrderCard_Title_CancelAppointment') : null}
							leftButtonMobileText={
								this.props?.mode === 'edit' ? t('Orders_UserOrderCard_Title_CancelAppointment') : null
							}
							leftButtonCustomClass={'red'}
							buttonsWrapperCustomClass={areOrderItemsInThePast ? 'rightAlign' : null}
						>
							{(this.props?.currentFormValues?.isRecurringEvent === true &&
								this.props?.currentFormValues?.updateOnlySelectedItem === true) ||
							areOrderItemsInThePast === true ? (
								<div className="main-booking-screen__warningWrapper">
									{renderIcon('warning', undefined, undefined, 'main-booking-screen__warningIcon')}
									<span className="main-booking-screen__warningText">
										{areOrderItemsInThePast
											? t('ManualBookingModal_MainScreen_Edit_PastAppointment')
											: t('ManualBookingModal_MainScreen_Edit_Tooltip_Text')}
									</span>
								</div>
							) : null}
							<ErrorSummary error={this.props.appointmentsError} message={this.props.appointmentsErrorMessage} />
							{(this.props.currentFormValues?.totalPrice != undefined && (
								<div className="main-booking-screen__footerTotalPrice">
									{this.props.currentFormValues.totalPrice != undefined ? (
										<span className="main-booking-screen__totalText">{t('BSPortal_Common_Total')}</span>
									) : null}
									<div className="main-booking-screen__footerPriceWrapper">
										{this.props.mode === 'edit' &&
											this.props.currentFormValues.totalPrice != undefined &&
											orderStatusElement}
										<span className="main-booking-screen__totalPrice">
											{formatCurrency(
												this.props?.manageShop?.shop?.currencyKey,
												this.props.currentFormValues.totalPrice,
											)}
										</span>
									</div>
								</div>
							)) ||
								undefined}
						</RightSideModalFooter>
					</>
				}
			>
				<LoadingOverlayWrapper
					active={
						this.state?.isSubmitting || this.props?.isShopOrderFetching || this.props.shopBookingTemplates.isFetching
					}
					spinner={true}
				>
					<div className="main-booking-screen__bodyWrapper">
						<BookingCustomer
							customer={customerData}
							namePlaceholder={
								this.props.mode === 'add'
									? t('ManualBookingModal_BookingCustomer_NoCustomer_Title')
									: this.props?.appointment?.customer?.name
							}
							subtitlePlaceholder={
								this.props.mode === 'add'
									? t('ManualBookingModal_BookingCustomer_NoCustomer_Subtitle')
									: this.props?.appointment?.customer?.email
							}
							onClick={
								this.props.currentFormValues?.customerId
									? undefined
									: () => setScreen(ManualBookingScreen.SelectCustomer)
							}
							dropdownOptions={
								this.props.mode === 'add'
									? [
											{
												text: (
													<a
														href={`${this.props.baseUrlClient}/shop/${this.props.shopId}/customer/${customerData?.id}?tab=appointments`}
														target="_blank"
														rel="noreferrer"
													>
														{t('ManualBookingModal_BookingCustomer_SelectCustomer_CustomerProfile')}
													</a>
												),
											},
											{
												text: t('ManualBookingModal_BookingCustomer_SelectCustomer_EditCustomer'),
												onClick: () =>
													this.props.setScreen(ManualBookingScreen.AddEditCustomer, {
														customerId: this.props.currentFormValues?.customerId,
														previousScreen: ManualBookingScreen.Main,
													}),
											},
											{
												text: t('ManualBookingModal_BookingCustomer_SelectCustomer_RemoveCustomer'),
												onClick: () => this.props.change('customerId', null),
												className: 'red',
											},
									  ]
									: [
											{
												text: (
													<a
														href={`${this.props.baseUrlClient}/shop/${this.props.shopId}/customer/${customerData?.id}?tab=appointments`}
														target="_blank"
														rel="noreferrer"
													>
														{t('ManualBookingModal_BookingCustomer_SelectCustomer_CustomerProfile')}
													</a>
												),
											},
											{
												text: t('ManualBookingModal_BookingCustomer_SelectCustomer_EditCustomer'),
												onClick: () =>
													this.props.setScreen(ManualBookingScreen.AddEditCustomer, {
														customerId: this.props.currentFormValues?.customerId,
														previousScreen: ManualBookingScreen.Main,
													}),
											},
									  ]
							}
							hideIcon={
								this.props?.mode === 'edit' && this.props?.shopcard?.selectedCustomer?.email === 'walk-in@glossiq.com'
							}
						/>
						<div className="manual-booking-modal__tabs">
							{mainTabs.map((tab) => {
								return (
									<div
										className={`manual-booking-modal__tab ${
											this.state.mainScreenCurrentTab === tab.key ? 'active' : ''
										}`}
										onClick={() => this.handleMainScreenTabSelect(tab.key)}
										key={tab.key}
									>
										{tab.translation}
										{tab?.numberSet && (
											<span className="manual-booking-modal__tab__numberOfItems">{tab.numberSet}</span>
										)}
									</div>
								);
							})}
						</div>
						{tabContent}
					</div>
				</LoadingOverlayWrapper>
			</RightSideModal>
		);
	}
}

const dispatchToProps = (dispatch: Dispatch<IStore> /*, ownProps: IOwnProps*/) => ({
	getOneShopCustomer: (customerId) => dispatch(getOneShopCustomer(customerId)),
});

const stateToProps = (state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: IManualBookingFormData = null;
	const currentOffice = state?.manageShop?.shop?.offices.find((off) => off.id === ownProps.officeId) || null;

	if (state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN] && state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN].values) {
		currentFormValues = state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN].values;
	}

	return {
		currentFormValues,
		shopcard: state.shopcard,
		shopOrder: state.shopOrderCard.shopOrder,
		isShopOrderFetching: state.shopOrderCard.isFetching,
		manageShop: state.manageShop,
		serviceGroups: state.serviceGroups,
		shopBookingTemplates: state.shopBookingTemplates,
		currentOffice,
		appointmentsError: state.appointments.error,
		appointmentsErrorMessage: state.appointments.message,
		baseUrlClient: state.fetchDefault.baseUrlClient,
	};
};

export const MainBookingScreen = connect(
	stateToProps,
	dispatchToProps,
)(
	reduxForm({
		form: c.FORM_NAME_MANUAL_BOOKING_PLAN,
		enableReinitialize: false,
		destroyOnUnmount: false,
		keepDirtyOnReinitialize: true,
	})(MainBookingScreenComponent),
);
